import { BitkitSelectMenuAction, BitkitUISideNav, IconPlus } from '@bitrise/bitkit-v2';
import { useContext, useMemo, useState } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import useAccounts from '@/hooks/useAccounts';
import useSwitchAccount from '@/hooks/useSwitchAccount';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { navigateToUrl } from '@/utils/navigation';

const WorkspaceSelector = () => {
  const { account } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const [selectedSlug, setSelectedSlug] = useState(account.slug);
  // Every page renders the nav, so the list is only worth fetching once someone opens the menu.
  const [wasOpened, setWasOpened] = useState(false);
  const { data: accounts, isError, isLoading } = useAccounts({ enabled: wasOpened });
  const { mutate: switchAccount } = useSwitchAccount();

  const workspaces = useMemo(
    () =>
      (accounts ?? [account]).map(({ avatarUrl, name, slug }) => ({
        avatar: avatarUrl ?? undefined,
        label: name,
        value: slug,
      })),
    [account, accounts],
  );

  const onWorkspaceSelect = (slug: string) => {
    setSelectedSlug(slug);
    switchAccount(
      { slug },
      {
        onError: () => setSelectedSlug(account.slug),
        onSuccess: ({ headers }) => navigateToUrl(headers.location),
      },
    );
  };

  return (
    <BitkitUISideNav.WorkspaceSelector
      className="platform-nav-workspace"
      errorLabel={isError ? 'Failed to load workspaces.' : undefined}
      errorHelperText={isError ? 'Try refreshing the page.' : undefined}
      isLoading={isLoading}
      onOpenChange={(isOpen) => isOpen && setWasOpened(true)}
      onValueChange={onWorkspaceSelect}
      value={selectedSlug}
      workspaces={workspaces}
    >
      {!isError && <BitkitSelectMenuAction href="/create-workspace" icon={IconPlus} label="Create workspace" />}
    </BitkitUISideNav.WorkspaceSelector>
  );
};

export default WorkspaceSelector;
